/**
 * Dead-letter requeue panel — task-urnb2ftcux (operator controls build 3).
 *
 * The first UI on POST /api/admin/search-platform/dead-letter/requeue. Every guard
 * lives in the service (cohort registry, approval gate, bounded limit, dry-run
 * default); this panel adds the operator discipline the console press could not
 * enforce: a census to read first, a PREVIEW (dryRun:true) before any commit, a
 * confirmation that states the exact row count, and the route's real answer shown
 * verbatim — including a 500's "outcome unknown" and its correlationId.
 *
 * The commit is the ONLY call that sends dryRun:false, and it is enabled only after
 * a preview at the same site, cohort and limit. Changing any of them re-disables it.
 *
 * codex round 1 @40eb46845 (both legs):
 *  - a transport failure during the commit is an UNKNOWN outcome (the server may have
 *    committed): it is shown as such, the dialog closes and the preview is invalidated,
 *    so no second click can requeue the NEXT batch. Every attempted commit — success,
 *    HTTP error, rejection — invalidates the preview; only a fresh preview re-arms.
 *  - the dialog authorises UP TO the limit and names the preview as a snapshot: the
 *    service selects a fresh batch at commit time, so the exact count cannot be
 *    promised without binding the write to the previewed ids (a #6115 contract change,
 *    not taken in this round). The result shows what was actually requeued.
 *  - census state is scoped to the site: a load sequence discards stale responses and
 *    every site-specific state resets when siteId changes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface CohortSpec { name: string; errorPrefix: string; causeFixed: boolean; requeueApproved: boolean }
interface CensusRow { siteId: string; siteName: string | null; cohort: string; count: number; oldest: string | null; newest: string | null }
interface QueueRow { siteId: string; status: string; count: number }
interface Census { siteId: string | null; cohorts: CohortSpec[]; rows: CensusRow[]; queue: QueueRow[] }
interface RequeueResult {
  cohort: string; limit: number; siteId: string | null; dryRun: boolean;
  eligible: number; superseded: number; requeued: number; wouldRequeue: number;
  redundantSiblings?: number; contended: boolean; causeFixed: boolean; requeueApproved: boolean; collisionRetryable: boolean;
}
interface RouteError { error: string; outcome?: string; correlationId?: string }

const MAX_BATCH = 1000;

// codex round 2 @ef08430e4: a body that cannot be read is a FAILURE, never a value.
// The previous helper swallowed the decode error into { error } and the caller then
// checked only res.ok, so a truncated HTTP 200 became a "preview" (undefined === 0 is
// false → commit enabled) and a broken commit response rendered "Requeued".
type DecodeOk = { ok: true; body: unknown };
type DecodeFail = { ok: false; reason: string };
type Decoded = DecodeOk | DecodeFail;
const failed = (d: Decoded): d is DecodeFail => d.ok === false;
async function readJson(res: Response): Promise<Decoded> {
  try {
    return { ok: true, body: await res.json() };
  } catch (err) {
    return { ok: false, reason: `HTTP ${res.status} body could not be read as JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
}
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
// The success shape the service documents. Counts MUST be numbers; a missing count
// is a malformed response, not zero.
function asRequeueResult(body: unknown): RequeueResult | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (typeof b.cohort !== 'string' || typeof b.dryRun !== 'boolean') return null;
  if (!isNum(b.eligible) || !isNum(b.superseded) || !isNum(b.requeued) || !isNum(b.wouldRequeue) || !isNum(b.limit)) return null;
  return b as unknown as RequeueResult;
}
function routeErrorOf(body: unknown, status: number): RouteError {
  const e = (body && typeof body === 'object' ? body : {}) as Partial<RouteError>;
  return { error: typeof e.error === 'string' ? e.error : `HTTP ${status}`, outcome: e.outcome, correlationId: e.correlationId };
}

export function DeadLetterRequeuePanel({ siteId, siteName }: { siteId: string; siteName: string }) {
  const [cohorts, setCohorts] = useState<CohortSpec[]>([]);
  const [census, setCensus] = useState<Census | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<RouteError | null>(null);

  const [cohort, setCohort] = useState<string>('');
  const [limitText, setLimitText] = useState<string>('1');
  // codex round 4 @1303b93da: parse the WHOLE value. parseInt read '1.9' and '1e2' as 1,
  // so a truncated edit could keep an earlier preview current. An operator batch size is
  // plain digits: anything else (fraction, exponent, sign, blank) is invalid, not rounded.
  const limit = /^\d+$/.test(limitText.trim()) ? Number(limitText.trim()) : NaN;
  const limitValid = Number.isInteger(limit) && limit >= 1 && limit <= MAX_BATCH;

  const [preview, setPreview] = useState<RequeueResult | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);
  const [routeError, setRouteError] = useState<RouteError | null>(null);
  const [committed, setCommitted] = useState<RequeueResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // keyed on the parsed integer: with the digits-only parse above, text that is not a
  // plain integer is NaN (never current), and equal integers mean an equal request.
  const currentKey = `${siteId}|${cohort}|${limit}`;
  const previewIsCurrent = preview !== null && previewKey === currentKey;

  // Monotonic load sequence: a response is applied only if it belongs to the latest
  // load, so a slow answer for a previous site can never overwrite the current one.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const [cRes, sRes] = await Promise.all([
        authFetch('/api/admin/search-platform/dead-letter/cohorts'),
        authFetch(`/api/admin/search-platform/dead-letter/census?siteId=${encodeURIComponent(siteId)}`),
      ]);
      const cDec = await readJson(cRes);
      const sDec = await readJson(sRes);
      if (seq !== loadSeq.current) return; // stale: a newer load (another site) superseded this one
      if (failed(cDec)) { setLoadError({ error: `cohorts: ${cDec.reason}` }); return; }
      if (failed(sDec)) { setLoadError({ error: `census: ${sDec.reason}` }); return; }
      // a failed read keeps the route's correlationId — the message tells the operator to search for it
      if (!cRes.ok) { setLoadError(routeErrorOf(cDec.body, cRes.status)); return; }
      if (!sRes.ok) { setLoadError(routeErrorOf(sDec.body, sRes.status)); return; }
      const cBody = cDec.body as { cohorts?: CohortSpec[] };
      const sBody = sDec.body as Census;
      if (!Array.isArray(cBody?.cohorts) || !Array.isArray(sBody?.rows)) {
        setLoadError({ error: 'malformed census response (HTTP 200 without cohorts/rows)' });
        return;
      }
      setCohorts(cBody.cohorts);
      setCensus(sBody);
      setCohort(prev => prev || (sBody.rows[0]?.cohort ?? cBody.cohorts?.[0]?.name ?? ''));
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setLoadError({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [siteId]);

  // Site change: every site-specific state goes, then the census loads for the new site.
  useEffect(() => {
    setCensus(null);
    setPreview(null);
    setPreviewKey(null);
    setCommitted(null);
    setRouteError(null);
    void load();
  }, [siteId, load]);

  const cohortSpec = useMemo(() => cohorts.find(c => c.name === cohort) || null, [cohorts, cohort]);

  const post = async (dryRun: boolean): Promise<RequeueResult | null> => {
    setRouteError(null);
    let res: Response;
    try {
      res = await authFetch('/api/admin/search-platform/dead-letter/requeue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cohort, limit, dryRun, siteId }),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // A dry run writes nothing, so a failed preview is just a failed read. A failed
      // COMMIT is an unknown outcome: the request may have reached the server and
      // committed before the response was lost.
      setRouteError(dryRun
        ? { error: `Transport failure during the preview: ${msg}. Nothing was written (dry run). Retry the preview.` }
        : { error: `Transport failure during the commit: ${msg}. OUTCOME UNKNOWN — the server may have committed this batch. Reload the census and verify before retrying.`, outcome: 'unknown' });
      return null;
    }
    const dec = await readJson(res);
    if (failed(dec)) {
      // The status line arrived but the body did not. For a dry run that is a failed
      // read; for a commit the write may well have happened: OUTCOME UNKNOWN.
      setRouteError(dryRun
        ? { error: `Preview response unreadable: ${dec.reason}. Nothing was written (dry run). Retry the preview.` }
        : { error: `Commit response unreadable: ${dec.reason}. OUTCOME UNKNOWN — the batch may have been requeued. Reload the census and verify before retrying.`, outcome: 'unknown' });
      return null;
    }
    if (!res.ok) {
      setRouteError(routeErrorOf(dec.body, res.status));
      return null;
    }
    const result = asRequeueResult(dec.body);
    if (!result) {
      setRouteError(dryRun
        ? { error: `Malformed preview response (HTTP ${res.status}): the counts are missing or not numbers. Nothing was written (dry run).` }
        : { error: `Malformed commit response (HTTP ${res.status}): the requeued count is missing. OUTCOME UNKNOWN — reload the census and verify before retrying.`, outcome: 'unknown' });
      return null;
    }
    return result;
  };

  const runPreview = async () => {
    setBusy('preview');
    setCommitted(null);
    // codex round 3 @d82ebc873 (both legs): a preview that FAILS must not leave the
    // earlier successful one authorising the commit. Clear before the call.
    setPreview(null);
    setPreviewKey(null);
    try {
      const out = await post(true);
      if (out) { setPreview(out); setPreviewKey(currentKey); }
    } finally { setBusy(null); }
  };

  const runCommit = async () => {
    setBusy('commit');
    try {
      const out = await post(false);
      // Every ATTEMPTED commit — success, HTTP error, transport failure — closes the
      // dialog and invalidates the preview. A second write needs a fresh preview.
      setShowConfirm(false);
      setPreview(null);
      setPreviewKey(null);
      if (out) {
        setCommitted(out);
        await load();
      }
    } finally { setBusy(null); }
  };

  const rowsForSite = census?.rows ?? [];
  const queueForSite = census?.queue ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Search export dead letters — {siteName}
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading} aria-label="Reload census">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </CardTitle>
        <CardDescription>
          Rows the Vertex export gave up on (MAX_ATTEMPTS spent). Nothing retries them until an operator
          requeues a cohort. Preview first; the commit only moves what the preview counted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loadError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not load</AlertTitle>
            <AlertDescription className="space-y-1">
              <div className="whitespace-pre-wrap font-mono text-xs">{loadError.error}</div>
              {loadError.correlationId && <div className="font-mono text-xs">correlationId: {loadError.correlationId}</div>}
            </AlertDescription>
          </Alert>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cohort</TableHead>
                <TableHead>Dead-lettered</TableHead>
                <TableHead>Oldest</TableHead>
                <TableHead>Newest</TableHead>
                <TableHead>Cause fixed</TableHead>
                <TableHead>Requeue approved</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {census !== null && rowsForSite.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} className="text-muted-foreground">No dead-lettered rows for this site.</TableCell></TableRow>
              )}
              {rowsForSite.map(r => {
                const spec = cohorts.find(c => c.name === r.cohort);
                return (
                  <TableRow key={r.cohort} data-state={r.cohort === cohort ? 'selected' : undefined} className="cursor-pointer" onClick={() => setCohort(r.cohort)}>
                    <TableCell className="font-mono">{r.cohort}</TableCell>
                    <TableCell>{r.count.toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{r.oldest ? new Date(r.oldest).toISOString().slice(0, 16).replace('T', ' ') : '—'}</TableCell>
                    <TableCell className="text-xs">{r.newest ? new Date(r.newest).toISOString().slice(0, 16).replace('T', ' ') : '—'}</TableCell>
                    <TableCell>{spec ? <Badge variant={spec.causeFixed ? 'default' : 'secondary'}>{spec.causeFixed ? 'yes' : 'no'}</Badge> : '—'}</TableCell>
                    <TableCell>{spec ? <Badge variant={spec.requeueApproved ? 'default' : 'destructive'}>{spec.requeueApproved ? 'yes' : 'no'}</Badge> : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {queueForSite.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Live queue for this site: {queueForSite.map(q => `${q.status} ${q.count.toLocaleString()}`).join(' · ')}
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-3 items-end">
          <div>
            <Label htmlFor="dl-cohort">Cohort</Label>
            <select
              id="dl-cohort"
              className="w-full h-10 rounded-md border bg-background px-3 text-sm"
              value={cohort}
              onChange={e => setCohort(e.target.value)}
            >
              {cohorts.map(c => (
                <option key={c.name} value={c.name}>
                  {c.name}{c.requeueApproved ? '' : ' (not approved — dry run only)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="dl-limit">Limit (1–{MAX_BATCH}, start with 1)</Label>
            <Input id="dl-limit" type="number" min={1} max={MAX_BATCH} value={limitText} onChange={e => setLimitText(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void runPreview()} disabled={busy !== null || !cohort || !limitValid}>
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Preview (dry run)
            </Button>
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={busy !== null || !previewIsCurrent || !preview || preview.wouldRequeue === 0 || !cohortSpec?.requeueApproved}
            >
              {previewIsCurrent && preview ? `Requeue up to ${limit} row${limit === 1 ? '' : 's'}` : 'Requeue (preview first)'}
            </Button>
          </div>
        </div>

        {preview && previewIsCurrent && (
          <Alert>
            <AlertTitle>Preview — nothing written</AlertTitle>
            <AlertDescription>
              Would requeue <strong>{preview.wouldRequeue}</strong> of {preview.eligible} eligible rows in cohort{' '}
              <span className="font-mono">{preview.cohort}</span> for {siteName} (limit {preview.limit}).
              {preview.superseded > 0 && <> {preview.superseded} skipped: a newer pending row exists.</>}
              {preview.redundantSiblings ? <> {preview.redundantSiblings} duplicate-product rows in the cohort.</> : null}
              {!cohortSpec?.requeueApproved && <> This cohort is <strong>not approved</strong> for requeue; the commit stays disabled.</>}
            </AlertDescription>
          </Alert>
        )}

        {committed && (
          <Alert>
            <AlertTitle>Requeued {committed.requeued}</AlertTitle>
            <AlertDescription>
              {committed.requeued} of {committed.wouldRequeue} targeted rows are pending again (cohort{' '}
              <span className="font-mono">{committed.cohort}</span>, {siteName}).
              {committed.contended && <> Batch was <strong>contended</strong>: another caller holds the rest — not exhaustion.</>}
              {committed.collisionRetryable && <> A concurrent pending sibling collided — nothing written, retry.</>}
            </AlertDescription>
          </Alert>
        )}

        {routeError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{routeError.outcome === 'unknown' ? 'Outcome UNKNOWN' : 'Request refused'}</AlertTitle>
            <AlertDescription className="space-y-1">
              <div className="whitespace-pre-wrap font-mono text-xs">{routeError.error}</div>
              {routeError.correlationId && <div className="font-mono text-xs">correlationId: {routeError.correlationId}</div>}
            </AlertDescription>
          </Alert>
        )}

        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Confirm requeue
              </DialogTitle>
              <DialogDescription>
                This sets <strong>up to {limit} dead-lettered row{limit === 1 ? '' : 's'}</strong> of{' '}
                <strong>{siteName}</strong> in cohort <span className="font-mono">{cohort}</span> back to pending with attempts reset to 0.
                The preview counted <strong>{preview?.wouldRequeue ?? 0}</strong> eligible (of {preview?.eligible ?? 0}) as a snapshot;
                the commit selects a fresh batch of up to {limit}, and the result will show what was actually requeued.
                The export worker retries them on its next cycle. This is a production write.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={busy === 'commit'}>Keep them dead-lettered</Button>
              <Button onClick={() => void runCommit()} disabled={busy === 'commit'}>
                {busy === 'commit' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm requeue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default DeadLetterRequeuePanel;
